import {Button, Card, Text} from "@nike/eds";
import {edsSpace16} from "@nike/eds/dist/tokens";
import React, {useContext, useEffect, useState} from "react";
import {TransactionHistoryTable} from "./transaction-history/TransactionHistoryTable";
import SnackbarContext from "../../providers/snackbar-context";
import {useNavigate, useParams} from "react-router-dom";
import {CaretRight} from "@nike/nike-design-system-icons";
import "./InventoryItemDetail.css";
import {TransactionHistorySearchForm} from "./transaction-history/TransactionHistorySearchForm";
import {SearchForm} from "../../common/model/SearchForm";
import {LocationDetail} from "../shared/LocationDetail";
import useSWR from "swr";
import {QuantityAdjustment} from "./adjust-quantity/QuantityAdjustment";
import { useChatContext } from '@nike/ai-chat-widget/react'

function InventoryItemDetail({inventoryService, LocationTracker, host, permissions}) {
    const { updateContext } = useChatContext();
    const params = useParams();
    const [ searchForm, setSearchForm ] = useState<SearchForm | undefined>(undefined);

    const snackbarCtx = useContext(SnackbarContext);
    const [showQuantityAdjustment, setShowQuantityAdjustment] = useState(false);

    const searchHandler = (searchFormData: SearchForm, restore : boolean) => {
        setSearchForm(searchFormData);
    }

    const transactionFetcher = async ({searchData, skuData}) => {
        if (searchData) {
            return await inventoryService
            .listTransactions(skuData.materialNumber, skuData.size, skuData.countryOfOrigin, skuData.quality, searchData.startDate,
                searchData.endDate, searchData.location, searchData.customsStatus, searchData.type, searchData.reason, searchData.source);
        }
        return await inventoryService
            .listTransactions(skuData.materialNumber, skuData.size, skuData.countryOfOrigin, skuData.quality);
    }

    const serverSearchData: SearchForm | undefined = searchForm ? {
        startDate: searchForm.startDate,
        endDate: searchForm.endDate,
        location: searchForm.location,
        customsStatus: searchForm.customsStatus,
        type: searchForm.type,
        reason: searchForm.reason,
        source: searchForm.source
    } : undefined;

    const { data : transactionData, isLoading : transactionsLoading, error} = useSWR({
        type: "Transactions",
        searchData: serverSearchData,
        skuData: params
    }, transactionFetcher, {
        refreshInterval: 60 * 1000
    });

    let transactionHistoryResults = [];
    if (transactionData && transactionData.data) {
        transactionHistoryResults = transactionData.data.transactionsDynamicSearch.items
    }

    const matchesTriggerDetailFilter = (row) => {
        if (searchForm?.trigger && row.trigger !== searchForm.trigger) return false;
        if (searchForm?.detail && row.detail !== searchForm.detail) return false;
        return true;
    };

    const filteredTransactionResults = transactionHistoryResults.filter(matchesTriggerDetailFilter);

    useEffect(() => {
        if (error && snackbarCtx) {
                snackbarCtx.displayMsg("Failed to load transactions: " + error, "error");
        }
    }, [error, snackbarCtx]);

    useEffect(() => {
      // Context updates automatically passed to the widget
      updateContext({ sku: params }, 'sku');
    }, [params, updateContext])

    let navigate = useNavigate();

    const routeChange = () => {
        let path = '/inventory-overview';
        navigate(path);
    }

    const titleStyle = {
        marginBottom: edsSpace16
    }

    const boldStyle = {
        fontWeight: "bold"
    }

    const tableDiv = {
        marginTop: edsSpace16
    };

    const marginBottom2Rem = {
        marginBottom: "2rem"
    }

    const detailViewMargin = {
        marginBottom: "1rem"
    }

    const detailTopRow = {
        marginBottom: "4rem",
        width: "80%"
    }

    const buttonWidth = {
        width: "60%"
    }

    const handleAdjustQuantityClick = () => {
        setShowQuantityAdjustment(true);
    }

    return (
        <div>
            <LocationTracker location="Inventory Overview" sublocation="Detail"
                            material={params.materialNumber} size={params.size} coo={params.countryOfOrigin} />
            <Text style={titleStyle} font={"display-3"} as={"h3"}>ITEM DETAILS</Text>
            <Button style={marginBottom2Rem} variant={"ghost"} onClick={() => routeChange()}>
                Inventory
                <CaretRight size={"s"}/>
            </Button>
            {params &&
                <div style={detailTopRow} className="eds-grid eds-grid--m-cols-3">
                    <div className="eds-grid eds-grid--m-row-1">
                        <div className="eds-grid_item eds-grid_item--m-1-2">
                            <Card>
                                <div style={detailViewMargin} className="eds-grid eds-grid--m-cols-2">
                                    <Text>Material</Text>
                                    <Text style={boldStyle}>{params.materialNumber}</Text>
                                </div>
                                <div style={detailViewMargin} className="eds-grid eds-grid--m-cols-2">
                                    <Text>Size</Text>
                                    <Text style={boldStyle}>{params.size}</Text>
                                </div>
                                <div style={detailViewMargin} className="eds-grid eds-grid--m-cols-2">
                                    <Text>Country Of Origin</Text>
                                    <Text style={boldStyle}>{params.countryOfOrigin}</Text>
                                </div>
                                <div className="eds-grid eds-grid--m-cols-2">
                                    <Text>Quality</Text>
                                    <Text style={boldStyle}>{params.quality}</Text>
                                </div>
                            </Card>
                        </div>

                        {permissions.canAdjustQuantity && (
                            <div className="eds-grid_item eds-grid_item--m-1-2">
                                <Button style={buttonWidth} className="eds-grid--m-col-1" variant="primary" type="submit" onClick={handleAdjustQuantityClick}>
                                    Adjust Quantity
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="eds-grid eds-grid--m-col-2">
                            <LocationDetail sku={{
                                material: params.materialNumber,
                                size: params.size,
                                countryOfOrigin: params.countryOfOrigin,
                                quality: params.quality,
                            }} />
                    </div>
                </div>
            }

            {!showQuantityAdjustment && (
                <div style={tableDiv}>
                    <Text style={titleStyle} font={"title-3"} as={"h3"}>Transaction History</Text>
                    <TransactionHistorySearchForm onSearch={searchHandler}/>
                    <TransactionHistoryTable searchResults={filteredTransactionResults} loading={transactionsLoading}/>
                </div>
            )}

            {showQuantityAdjustment && (
                <div>
                    <QuantityAdjustment inventoryService={inventoryService} onClose={() => setShowQuantityAdjustment(false)} host={host} />
                </div>
            )}

        </div>
    )
}

export {InventoryItemDetail};